import React, { useMemo } from 'react';
import { LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import Icon from '../../../components/AppIcon';

const TrendsVisualization = ({ referrals }) => {
  // Calculate monthly trends
  const monthlyTrends = useMemo(() => {
    const monthlyData = {};
    const now = new Date();
    
    // Initialize last 6 months
    for (let i = 5; i >= 0; i--) {
      const date = new Date(now?.getFullYear(), now?.getMonth() - i, 1);
      const monthKey = date?.toLocaleDateString('fr-FR', { month: 'short', year: 'numeric' });
      monthlyData[monthKey] = { month: monthKey, total: 0, completed: 0, pending: 0 };
    }

    // Count referrals by month
    referrals?.forEach(ref => {
      const refDate = new Date(ref?.submissionDate);
      const monthKey = refDate?.toLocaleDateString('fr-FR', { month: 'short', year: 'numeric' });
      
      if (monthlyData?.[monthKey]) {
        monthlyData[monthKey].total++;
        if (ref?.status === 'completed') {
          monthlyData[monthKey].completed++;
        } else if (['new', 'reviewed']?.includes(ref?.status)) {
          monthlyData[monthKey].pending++;
        }
      }
    });

    return Object.values(monthlyData);
  }, [referrals]);

  // Calculate top referring dentists
  const topDentists = useMemo(() => {
    const dentistCounts = {};
    
    referrals?.forEach(ref => {
      const name = ref?.referringDentistName || 'Inconnu';
      if (!dentistCounts?.[name]) {
        dentistCounts[name] = { name, count: 0 };
      }
      dentistCounts[name].count++;
    });

    return Object.values(dentistCounts)
      ?.sort((a, b) => b?.count - a?.count)
      ?.slice(0, 5);
  }, [referrals]);

  // Calculate common treatment patterns
  const treatmentPatterns = useMemo(() => {
    const reasonCounts = {};
    
    referrals?.forEach(ref => {
      ref?.referralReasons?.forEach(reason => {
        if (!reasonCounts?.[reason]) {
          reasonCounts[reason] = { reason, count: 0 };
        }
        reasonCounts[reason].count++;
      });
    });

    return Object.values(reasonCounts)
      ?.sort((a, b) => b?.count - a?.count)
      ?.slice(0, 5);
  }, [referrals]);

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      {/* Monthly Trends Chart */}
      <div className="bg-[rgba(26,26,30,0.95)] backdrop-blur-2xl border border-white/10 rounded-2xl p-6 shadow-2xl">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 rounded-xl bg-blue-500/10 flex items-center justify-center">
            <Icon name="TrendingUp" size={20} className="text-blue-400" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-white">Tendances Mensuelles</h3>
            <p className="text-sm text-gray-400">Évolution des adressages</p>
          </div>
        </div>

        <ResponsiveContainer width="100%" height={300}>
          <LineChart data={monthlyTrends}>
            <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.1)" />
            <XAxis 
              dataKey="month" 
              stroke="#9ca3af"
              style={{ fontSize: '12px' }}
            />
            <YAxis 
              stroke="#9ca3af"
              style={{ fontSize: '12px' }}
            />
            <Tooltip 
              contentStyle={{
                backgroundColor: 'rgba(26, 26, 30, 0.95)',
                border: '1px solid rgba(255, 255, 255, 0.1)',
                borderRadius: '12px',
                color: '#e5e7eb'
              }}
            />
            <Legend />
            <Line 
              type="monotone" 
              dataKey="total" 
              stroke="#60a5fa" 
              strokeWidth={3}
              name="Total"
              dot={{ fill: '#60a5fa', r: 5 }}
            />
            <Line 
              type="monotone" 
              dataKey="completed" 
              stroke="#10b981" 
              strokeWidth={2}
              name="Terminés"
              dot={{ fill: '#10b981', r: 4 }}
            />
            <Line 
              type="monotone" 
              dataKey="pending" 
              stroke="#f59e0b" 
              strokeWidth={2}
              name="En attente"
              dot={{ fill: '#f59e0b', r: 4 }}
            />
          </LineChart>
        </ResponsiveContainer>
      </div>

      {/* Top Referring Dentists */}
      <div className="bg-[rgba(26,26,30,0.95)] backdrop-blur-2xl border border-white/10 rounded-2xl p-6 shadow-2xl">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 rounded-xl bg-green-500/10 flex items-center justify-center">
            <Icon name="Users" size={20} className="text-green-400" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-white">Top Dentistes Adresseurs</h3>
            <p className="text-sm text-gray-400">Par volume d'adressages</p>
          </div>
        </div>

        <ResponsiveContainer width="100%" height={300}>
          <BarChart data={topDentists}>
            <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.1)" />
            <XAxis 
              dataKey="name" 
              stroke="#9ca3af"
              style={{ fontSize: '11px' }}
              angle={-45}
              textAnchor="end"
              height={100}
            />
            <YAxis 
              stroke="#9ca3af"
              style={{ fontSize: '12px' }}
            />
            <Tooltip 
              contentStyle={{
                backgroundColor: 'rgba(26, 26, 30, 0.95)',
                border: '1px solid rgba(255, 255, 255, 0.1)',
                borderRadius: '12px',
                color: '#e5e7eb'
              }}
            />
            <Bar 
              dataKey="count" 
              fill="#10b981" 
              name="Adressages" 
              radius={[8, 8, 0, 0]}
            />
          </BarChart>
        </ResponsiveContainer>
      </div>

      {/* Common Treatment Patterns */}
      <div className="bg-[rgba(26,26,30,0.95)] backdrop-blur-2xl border border-white/10 rounded-2xl p-6 shadow-2xl lg:col-span-2">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 rounded-xl bg-purple-500/10 flex items-center justify-center">
            <Icon name="Activity" size={20} className="text-purple-400" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-white">Motifs d'Adressage Fréquents</h3>
            <p className="text-sm text-gray-400">Distribution des traitements</p>
          </div>
        </div>

        <div className="space-y-3">
          {treatmentPatterns?.map((pattern, index) => {
            const maxCount = treatmentPatterns?.[0]?.count || 1;
            const percentage = (pattern?.count / maxCount) * 100;
            
            return (
              <div key={index} className="flex items-center gap-4">
                <span className="text-sm font-medium text-gray-300 w-48 truncate">
                  {pattern?.reason}
                </span>
                <div className="flex-1 bg-[rgba(18,18,22,0.95)] rounded-full h-8 overflow-hidden border border-white/10">
                  <div 
                    className="h-full bg-gradient-to-r from-purple-500 to-purple-400 flex items-center justify-end px-3"
                    style={{ width: `${percentage}%`, minWidth: pattern?.count > 0 ? '40px' : '0' }}
                  >
                    <span className="text-xs font-semibold text-white">{pattern?.count}</span>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default TrendsVisualization;